import React, { ComponentPropsWithoutRef, FC } from 'react';
import { Link, NavigateOptions } from 'react-router-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';

import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip/Tooltip';

type TablePropsType = {
  children: React.ReactNode;
};

const TableWrapper = ({ children }: TablePropsType) => {
  return (
    <div className="-mx-6 overflow-x-auto px-6">
      <div className={cn('inline-block min-w-full overflow-hidden  rounded-md  border border-gray-200 align-middle')}>
        <table className={cn('min-w-full  border-collapse')}>{children}</table>
      </div>
    </div>
  );
};

const Head = ({ children }: { children: React.ReactNode }) => <thead>{children}</thead>;

const HeadCell = ({
  invisible = false,
  alignRight = false,
  className = '',
  children,
  tooltip,
  ...otherProps
}: {
  invisible?: boolean;
  alignRight?: boolean;
  className?: string;
  children: React.ReactNode;
  tooltip?: string;
} & ComponentPropsWithoutRef<'th'>) =>
  invisible ? (
    <th scope="col" className={cn('relative border-b border-gray-200 px-3 py-4', className)} {...otherProps}>
      <span className="sr-only">{children}</span>
    </th>
  ) : (
    <th
      scope="col"
      className={cn(
        'border-b border-gray-200 px-3 py-4 text-left text-xs font-normal text-gray-500',
        alignRight ? 'text-right' : '',
        className,
      )}
      {...otherProps}
    >
      <div className={cn('flex', alignRight ? 'justify-end' : undefined)}>
        {children}

        {Boolean(tooltip) && (
          <div className="ml-1 cursor-context-menu">
            <Tooltip>
              <TooltipTrigger>
                <InformationCircleIcon className="mr-2 size-4 text-gray-400 hover:text-procuros-green-500" />
              </TooltipTrigger>
              <TooltipContent>{tooltip}</TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    </th>
  );

const Body = ({ children, isLoading = false }: { children: React.ReactNode; isLoading?: boolean }) => {
  return isLoading ? <BodyLoadingGlimmer /> : <tbody>{children}</tbody>;
};

const Footer = ({ children }: { children: React.ReactNode }) => <tfoot>{children}</tfoot>;

const Row = ({
  children,
  hover,
  onClick,
  className,
  withBottomBorder = true,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  hover?: boolean;
  className?: string;
  withBottomBorder?: boolean;
}) => (
  <tr
    className={cn(
      'text-sm text-gray-700 last:border-b-0 odd:bg-white even:bg-slate-50',
      withBottomBorder ? 'border-b border-gray-200' : undefined,
      hover ? 'group' : undefined,
      className,
    )}
    onClick={onClick}
  >
    {children}
  </tr>
);

const Cell: FC<
  {
    className?: string;
    to?: string | { url: string; state?: NavigateOptions['state'] };
    withHighlight?: boolean;
  } & ComponentPropsWithoutRef<'td'>
> = ({ children, className, to, withHighlight, ...otherProps }) => (
  <td
    {...otherProps}
    className={cn(
      'text-sm whitespace-nowrap text-gray-500 ',
      to ? 'p-0' : 'px-3 py-4',
      withHighlight === undefined
        ? undefined
        : withHighlight
          ? 'font-medium text-gray-900'
          : 'font-normal! text-gray-500!',
      className ?? '',
      'group-hover:cursor-pointer group-hover:bg-procuros-green-50 group-hover:text-procuros-green-600',
    )}
  >
    {to ? (
      <Link
        to={typeof to === 'string' ? to : to.url}
        state={typeof to === 'string' ? undefined : to.state}
        className="block w-full truncate px-3 py-4"
      >
        {children}
      </Link>
    ) : (
      children
    )}
  </td>
);

export const Table = Object.assign(TableWrapper, { Head, HeadCell, Body, Footer, Row, Cell });

const BodyLoadingGlimmer = () => {
  return (
    <tbody data-testid="table-loading-state">
      <tr>
        <td colSpan={99}>
          <div className="m-4 flex w-full flex-wrap">
            <div className="mb-3 block h-5 w-[95%] animate-pulse bg-gray-200 leading-relaxed"></div>
            <div className="mb-3 block h-5 w-[95%] animate-pulse bg-gray-100 leading-relaxed"></div>
            <div className="mb-3 block h-5 w-[95%] animate-pulse bg-gray-300 leading-relaxed"></div>
            <div className="mb-3 block h-5 w-[95%] animate-pulse bg-gray-200 leading-relaxed"></div>
          </div>
        </td>
      </tr>
    </tbody>
  );
};
